import { useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Boxes } from "lucide-react";
import { NAV, NAV_GROUPS, ROLE_LABELS } from "@/components/layout/navConfig";
import { SideNav, MobileNav } from "@/components/layout/SideNav";
import { LiveClock } from "@/components/layout/LiveClock";
import { NotificationBell } from "@/components/layout/NotificationBell";
import { ChangePasswordDialog } from "@/components/layout/ChangePasswordDialog";
import { OfflineBanner } from "@/components/layout/OfflineBanner";

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [pwOpen, setPwOpen] = useState(false);
  const items = NAV.filter((n) => n.roles.includes("*") || n.roles.includes(user?.role));
  const MOBILE = ["/", "/tm/my", "/tm/standup", "/tm/eod", "/tm/notifications"];
  const mobileItems = MOBILE.map((to) => items.find((n) => n.to === to)).filter(Boolean).concat(items.filter((n) => !MOBILE.includes(n.to))).slice(0, 5);
  const groups = NAV_GROUPS.map((g) => ({ ...g, items: g.items.filter((n) => n.roles.includes("*") || n.roles.includes(user?.role)) })).filter((g) => g.items.length > 0);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-background" data-testid="app-layout">
      <SideNav groups={groups} user={user} onChangePassword={() => setPwOpen(true)} onLogout={handleLogout} />
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />

      <div className="md:pl-60 flex flex-col min-h-screen">
        <header className="sticky top-0 z-30 h-14 flex items-center justify-between px-4 border-b border-border bg-card/80 backdrop-blur-md">
          <div className="flex items-center gap-2 md:hidden">
            <Boxes className="w-5 h-5 text-primary" />
            <span className="font-heading font-bold tracking-wide">WORLDITES</span>
          </div>
          <div className="hidden md:block text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
            Manufacturing · PET Bottles · Jars · Caps · Packaging
          </div>
          <div className="flex items-center gap-3">
            <NotificationBell />
            <LiveClock />
            <span className="hidden sm:inline text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded bg-primary/15 text-primary" data-testid="topbar-role-badge">
              {ROLE_LABELS[user?.role] || user?.role}
            </span>
          </div>
        </header>
        <OfflineBanner />

        <main className="flex-1 p-4 sm:p-6 pb-24 md:pb-6">
          <Outlet />
        </main>

        <MobileNav items={mobileItems} />
      </div>
    </div>
  );
}
